import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import { useHttpClient } from "shared/hooks/http-hook";
import MySkeleton from "components/ui/feedback/Skeleton";
import Button from "shared/components/FormElements/Button";
import SelectInput from "shared/components/FormElements/Select";
import CheckBox from "shared/components/FormElements/CheckBox";
import Input from "shared/components/FormElements/Input";
import { VALIDATOR_NONE } from "shared/util/validators";
import { PERMISSION_TYPES, COMPANY_FEATURES } from "@routebot/constants";
import AreYouSureModal from "components/shared/AreYouSureModal";

const CompanyManagement = () => {
  const { sendRequest } = useHttpClient();

  // Company states
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);

  // Client states
  const [clients, setClients] = useState([]);
  const [isClientsLoading, setIsClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [isSavingClient, setIsSavingClient] = useState(false);
  const [deleteTargetClientId, setDeleteTargetClientId] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Schools states
  const [schools, setSchools] = useState([]);
  const [isSchoolsLoading, setIsSchoolsLoading] = useState(false);

  // Summary & delete company states
  const [companySummary, setCompanySummary] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [showDeleteCompanyModal, setShowDeleteCompanyModal] = useState(false);
  const [isDeletingCompany, setIsDeletingCompany] = useState(false);

  // Editable company fields
  const [companyForm, setCompanyForm] = useState({});

  // Fetch companies on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/companies`);
        const list = responseData?.companies || [];
        const options = list.map((c) => ({
          value: c._id,
          label: `${c.name}${c.isDemo ? " (Demo)" : ""}${!c.isActive ? " (Pasif)" : ""}`,
        }));
        setCompanyOptions(options);
      } catch (err) {
        console.error("Failed to fetch companies:", err);
      }
    };
    fetchCompanies();
  }, [sendRequest]);

  // Fetch company details when selected
  useEffect(() => {
    if (!selectedCompanyId) {
      setClients([]);
      setSchools([]);
      setCompanyForm({});
      setCompanySummary(null);
      return;
    }

    const fetchCompanyDetails = async () => {
      setIsCompanyLoading(true);
      setIsClientsLoading(true);
      setIsSchoolsLoading(true);
      setIsSummaryLoading(true);
      try {
        const [companyRes, clientsRes, schoolsRes, summaryRes] = await Promise.all([
          sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/companies/${selectedCompanyId}`),
          sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/companies/${selectedCompanyId}/clients`),
          sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/companies/${selectedCompanyId}/schools`),
          sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/companies/${selectedCompanyId}/summary`),
        ]);
        setCompanyForm(companyRes?.company || {});
        setClients(clientsRes?.clients || []);
        setSchools(schoolsRes?.schools || []);
        setCompanySummary(summaryRes?.summary || null);
      } catch (err) {
        console.error("Failed to fetch company details:", err);
      } finally {
        setIsCompanyLoading(false);
        setIsClientsLoading(false);
        setIsSchoolsLoading(false);
        setIsSummaryLoading(false);
      }
    };
    fetchCompanyDetails();
  }, [selectedCompanyId, sendRequest]);

  const onCompanyChange = useCallback((id, value) => {
    setSelectedCompanyId(value);
    setSelectedClientId(null);
  }, []);

  const updateCompanyFormField = useCallback((field, value) => {
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateCompanyFeature = useCallback((feature, value) => {
    setCompanyForm((prev) => ({
      ...prev,
      features: { ...prev.features, [feature]: value },
    }));
  }, []);

  const saveCompany = useCallback(async () => {
    if (!selectedCompanyId || isSavingCompany) return;
    setIsSavingCompany(true);
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/admin/companies/${selectedCompanyId}`,
        "PATCH",
        JSON.stringify(companyForm),
        { "Content-Type": "application/json" },
      );
      setCompanyForm(response?.company || {});
      if (window?.toast?.success) window.toast.success("Şirket güncellendi.");

      // Update company options label
      setCompanyOptions((prev) =>
        prev.map((c) =>
          c.value === selectedCompanyId
            ? {
                ...c,
                label: `${response?.company?.name}${response?.company?.isDemo ? " (Demo)" : ""}${
                  !response?.company?.isActive ? " (Pasif)" : ""
                }`,
              }
            : c,
        ),
      );
    } catch (err) {
      console.error("Failed to save company:", err);
      if (window?.toast?.error) window.toast.error("Güncelleme başarısız.");
    } finally {
      setIsSavingCompany(false);
    }
  }, [selectedCompanyId, companyForm, sendRequest, isSavingCompany]);

  const updateClientPermission = useCallback(
    async (clientId, permissionKey, value) => {
      setIsSavingClient(true);
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/admin/clients/${clientId}`,
          "PATCH",
          JSON.stringify({ permissions: { [permissionKey]: value } }),
          { "Content-Type": "application/json" },
        );
        setClients((prev) => prev.map((c) => (c._id === clientId ? { ...c, ...response?.client } : c)));
        if (window?.toast?.success) window.toast.success("İzin güncellendi.");
      } catch (err) {
        console.error("Failed to update permission:", err);
        if (window?.toast?.error) window.toast.error("Güncelleme başarısız.");
      } finally {
        setIsSavingClient(false);
      }
    },
    [sendRequest],
  );

  const updateClientSchools = useCallback(
    async (clientId, schoolIds) => {
      setIsSavingClient(true);
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/admin/clients/${clientId}`,
          "PATCH",
          JSON.stringify({ schools: schoolIds }),
          { "Content-Type": "application/json" },
        );
        setClients((prev) => prev.map((c) => (c._id === clientId ? { ...c, ...response?.client } : c)));
        if (window?.toast?.success) window.toast.success("Okullar güncellendi.");
      } catch (err) {
        console.error("Failed to update schools:", err);
        if (window?.toast?.error) window.toast.error("Güncelleme başarısız.");
      } finally {
        setIsSavingClient(false);
      }
    },
    [sendRequest],
  );

  const toggleClientSchool = useCallback(
    (clientId, schoolId) => {
      const client = clients.find((c) => c._id === clientId);
      if (!client) return;

      const currentSchools = client.schools || [];
      const newSchools = currentSchools.includes(schoolId)
        ? currentSchools.filter((id) => id !== schoolId)
        : [...currentSchools, schoolId];

      updateClientSchools(clientId, newSchools);
    },
    [clients, updateClientSchools],
  );

  const handleHardDeleteClient = useCallback(async () => {
    if (!deleteTargetClientId) return;
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/clients/${deleteTargetClientId}`, "DELETE");
      setClients((prev) => prev.filter((c) => c._id !== deleteTargetClientId));
      if (window?.toast?.success) window.toast.success("Kullanıcı kalıcı olarak silindi.");
    } catch (err) {
      console.error("Failed to hard delete client:", err);
      if (window?.toast?.error) window.toast.error("Silme işlemi başarısız.");
    } finally {
      setDeleteTargetClientId(null);
    }
  }, [deleteTargetClientId, sendRequest]);

  const handleImpersonate = useCallback(
    async (clientId) => {
      if (isImpersonating) return;

      setIsImpersonating(true);
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/admin/impersonate/${clientId}`,
          "POST",
        );

        const { token, client } = responseData;

        // 1. Save current admin session
        const currentClientData = localStorage.getItem("clientData");
        if (currentClientData) {
          localStorage.setItem("adminClientData", currentClientData);
        }

        // 2. Set impersonation flags
        localStorage.setItem("isImpersonating", "true");
        localStorage.setItem("impersonatedClient", JSON.stringify(client));

        // 3. Set new token as active session
        localStorage.setItem(
          "clientData",
          JSON.stringify({
            clientId: client.id,
            token: token,
            expiration: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
          }),
        );

        // 4. Reload to apply new session
        window.location.href = "/";
      } catch (err) {
        console.error("Impersonation failed:", err);
        if (window?.toast?.error) {
          window.toast.error("Giriş yapılamadı.");
        }
        setIsImpersonating(false);
      }
    },
    [sendRequest, isImpersonating],
  );

  const handleDeleteCompany = useCallback(async () => {
    if (!selectedCompanyId || isDeletingCompany) return;
    setIsDeletingCompany(true);
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/companies/${selectedCompanyId}`, "DELETE");
      setCompanyOptions((prev) => prev.filter((c) => c.value !== selectedCompanyId));
      setSelectedCompanyId(null);
      setCompanySummary(null);
      if (window?.toast?.success) window.toast.success("Şirket ve tüm verileri kalıcı olarak silindi.");
    } catch (err) {
      console.error("Failed to delete company:", err);
      if (window?.toast?.error) window.toast.error("Şirket silme işlemi başarısız.");
    } finally {
      setIsDeletingCompany(false);
      setShowDeleteCompanyModal(false);
    }
  }, [selectedCompanyId, isDeletingCompany, sendRequest]);

  const summaryTotal = companySummary ? Object.values(companySummary).reduce((sum, count) => sum + count, 0) : 0;

  const SUMMARY_LABELS = {
    Client: "Kullanıcı",
    School: "Okul",
    User: "Kişi (Öğrenci/Veli/Personel)",
    Student: "Öğrenci",
    VehicleRoute: "Öğrenci Servisi",
    PersonelService: "Personel Servisi",
    Vehicle: "Araç",
    Contract: "Sözleşme",
    Receipt: "Tahsilat",
    Relation: "İlişki",
    Shift: "Vardiya",
    Transfer: "Transfer",
    Stop: "Durak",
    Stock: "Stok",
    Expense: "Gider",
  };

  return (
    <div style={{ padding: "20px", maxWidth: "1400px" }}>
      <h2 style={{ marginTop: 0, marginBottom: "20px" }}>Şirket Yönetimi</h2>

      <Grid container spacing={3}>
        {/* Company Selector */}
        <Grid item xs={12} md={6}>
          <SelectInput
            id="companySelect"
            label="Şirket Seçin"
            options={companyOptions}
            onInput={onCompanyChange}
            validators={[VALIDATOR_NONE()]}
            initialValue={selectedCompanyId ? companyOptions.find((c) => c.value === selectedCompanyId) : null}
          />
        </Grid>

        {selectedCompanyId && (
          <>
            {/* Company Details */}
            <Grid item xs={12}>
              <div
                style={{
                  background: "#f8f9fa",
                  padding: "20px",
                  borderRadius: "8px",
                  marginBottom: "10px",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: "20px" }}>Şirket Bilgileri</h3>

                {isCompanyLoading ? (
                  <MySkeleton lineCount={5} />
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Input
                        id="companyName"
                        element="input"
                        type="text"
                        label="Şirket Adı"
                        initialValue={companyForm.name || ""}
                        initialValid={true}
                        onInput={(id, value) => updateCompanyFormField("name", value)}
                        validators={[VALIDATOR_NONE()]}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Input
                        id="companyEmail"
                        element="input"
                        type="text"
                        label="Email"
                        initialValue={companyForm.email || ""}
                        initialValid={true}
                        onInput={(id, value) => updateCompanyFormField("email", value)}
                        validators={[VALIDATOR_NONE()]}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Input
                        id="companyPhone"
                        element="input"
                        type="text"
                        label="Telefon"
                        initialValue={companyForm.phonenumber || ""}
                        initialValid={true}
                        onInput={(id, value) => updateCompanyFormField("phonenumber", value)}
                        validators={[VALIDATOR_NONE()]}
                      />
                    </Grid>

                    {/* Temel Bilgiler - Sadece Görüntüleme */}
                    <Grid item xs={12}>
                      <div
                        style={{
                          display: "flex",
                          gap: "24px",
                          flexWrap: "wrap",
                          padding: "12px 16px",
                          background: "#e8eaf6",
                          borderRadius: "6px",
                          fontSize: "13px",
                        }}
                      >
                        <div>
                          <strong>Plan:</strong> {companyForm.plan || "-"}
                        </div>
                        <div>
                          <strong>Brand:</strong> {companyForm.brand || "-"}
                        </div>
                        {companyForm.country && (
                          <div>
                            <strong>Ülke:</strong> {companyForm.country}
                          </div>
                        )}
                        {companyForm.city && (
                          <div>
                            <strong>Şehir:</strong> {companyForm.city}
                          </div>
                        )}
                        {companyForm.createdAt && (
                          <div>
                            <strong>Kayıt:</strong>{" "}
                            {new Date(companyForm.createdAt).toLocaleString("sv-SE", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        )}
                        {companyForm.domains && (
                          <div>
                            <strong>Domains:</strong>{" "}
                            {[companyForm.domains.student && "Öğrenci", companyForm.domains.personel && "Personel"]
                              .filter(Boolean)
                              .join(", ") || "-"}
                          </div>
                        )}
                        {companyForm.subscriptionStatus && (
                          <div>
                            <strong>Abonelik:</strong> {companyForm.subscriptionStatus}
                          </div>
                        )}
                        {companyForm.currentPeriodEnd && (
                          <div>
                            <strong>Dönem Sonu:</strong>{" "}
                            {new Date(companyForm.currentPeriodEnd).toLocaleString("sv-SE", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })}
                          </div>
                        )}
                      </div>
                    </Grid>

                    <Grid item xs={12}>
                      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginTop: "10px" }}>
                        <CheckBox
                          id="isActive"
                          label="Aktif"
                          initialValue={companyForm.isActive ?? true}
                          initialValid={true}
                          onInput={(id, value) => updateCompanyFormField("isActive", value)}
                          validators={[VALIDATOR_NONE()]}
                        />
                        <CheckBox
                          id="isDemo"
                          label="Demo"
                          initialValue={companyForm.isDemo ?? false}
                          initialValid={true}
                          onInput={(id, value) => updateCompanyFormField("isDemo", value)}
                          validators={[VALIDATOR_NONE()]}
                        />
                        <CheckBox
                          id="isGolden"
                          label="VIP (Golden)"
                          initialValue={companyForm.isGolden ?? false}
                          initialValid={true}
                          onInput={(id, value) => updateCompanyFormField("isGolden", value)}
                          validators={[VALIDATOR_NONE()]}
                        />
                        <CheckBox
                          id="useSpecialDriver"
                          label="Özel Sürücü Kullan"
                          initialValue={companyForm.useSpecialDriver ?? false}
                          initialValid={true}
                          onInput={(id, value) => updateCompanyFormField("useSpecialDriver", value)}
                          validators={[VALIDATOR_NONE()]}
                        />
                      </div>
                    </Grid>

                    {/* Provider Bilgileri - Sadece Görüntüleme */}
                    {(companyForm.providerCustomerId ||
                      companyForm.providerSubscriptionId ||
                      companyForm.billingProvider) && (
                      <Grid item xs={12}>
                        <div
                          style={{
                            marginTop: "15px",
                            padding: "15px",
                            background: "#fff3e0",
                            borderRadius: "6px",
                            border: "1px solid #ffb74d",
                          }}
                        >
                          <h4 style={{ marginTop: 0, marginBottom: "10px", fontSize: "14px" }}>
                            Billing Provider Bilgileri (Sadece Görüntüleme)
                          </h4>
                          <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                            {companyForm.billingProvider && (
                              <div>
                                <strong>Provider:</strong> {companyForm.billingProvider}
                              </div>
                            )}
                            {companyForm.providerCustomerId && (
                              <div>
                                <strong>Customer ID:</strong> {companyForm.providerCustomerId}
                              </div>
                            )}
                            {companyForm.providerSubscriptionId && (
                              <div>
                                <strong>Subscription ID:</strong> {companyForm.providerSubscriptionId}
                              </div>
                            )}
                            {companyForm.providerSubscriptionStatus && (
                              <div>
                                <strong>Status:</strong> {companyForm.providerSubscriptionStatus}
                              </div>
                            )}
                          </div>
                        </div>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <h4 style={{ marginBottom: "10px" }}>Features</h4>
                      <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                        {Object.values(COMPANY_FEATURES).map((featureKey) => {
                          const featureValue = companyForm.features?.[featureKey] ?? false;
                          return (
                            <CheckBox
                              key={`${selectedCompanyId}-${featureKey}`}
                              id={`feature_${featureKey}`}
                              label={featureKey}
                              initialValue={featureValue}
                              initialValid={true}
                              onInput={(id, val) => updateCompanyFeature(featureKey, val)}
                              validators={[VALIDATOR_NONE()]}
                            />
                          );
                        })}
                      </div>
                    </Grid>

                    <Grid item xs={12}>
                      <Button type="button" onClick={saveCompany} disabled={isSavingCompany}>
                        {isSavingCompany ? "Kaydediliyor..." : "Şirketi Kaydet"}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </div>
            </Grid>

            {/* Company Data Summary */}
            <Grid item xs={12}>
              <div
                style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: "15px" }}>Veri Özeti</h3>

                {isSummaryLoading ? (
                  <MySkeleton lineCount={2} />
                ) : companySummary ? (
                  <>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
                      {Object.entries(companySummary)
                        .filter(([, count]) => count > 0)
                        .map(([key, count]) => (
                          <span
                            key={key}
                            style={{
                              padding: "4px 10px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              background: "#f5f5f5",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <strong>{count}</strong> {SUMMARY_LABELS[key] || key}
                          </span>
                        ))}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 16px",
                        background: "#ffebee",
                        borderRadius: "6px",
                        border: "1px solid #ef9a9a",
                      }}
                    >
                      <span style={{ fontSize: "13px", color: "#c62828" }}>
                        Toplam <strong>{summaryTotal}</strong> kayıt bu şirkete ait.
                      </span>
                      <button
                        onClick={() => setShowDeleteCompanyModal(true)}
                        disabled={isDeletingCompany}
                        style={{
                          background: "#d32f2f",
                          color: "#fff",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: "4px",
                          cursor: isDeletingCompany ? "not-allowed" : "pointer",
                          fontSize: "13px",
                          fontWeight: "bold",
                          opacity: isDeletingCompany ? 0.6 : 1,
                        }}
                      >
                        {isDeletingCompany ? "Siliniyor..." : "Şirketi ve Tüm Verileri Sil"}
                      </button>
                    </div>
                  </>
                ) : (
                  <p style={{ color: "#999", margin: 0 }}>Özet yüklenemedi.</p>
                )}

                <AreYouSureModal
                  showModal={showDeleteCompanyModal}
                  title="Şirketi Kalıcı Olarak Sil"
                  text={`"${companyForm.name || ""}" şirketini ve tüm verilerini kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz. Toplamda ${summaryTotal} kayıt silinecektir.`}
                  onConfirmed={handleDeleteCompany}
                  onClose={() => setShowDeleteCompanyModal(false)}
                />
              </div>
            </Grid>

            {/* Clients List */}
            <Grid item xs={12}>
              <div
                style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: "15px" }}>Kullanıcılar ({clients.length})</h3>

                {isClientsLoading ? (
                  <MySkeleton lineCount={5} />
                ) : clients.length === 0 ? (
                  <p style={{ color: "#999" }}>Bu şirkette kullanıcı yok.</p>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                      }}
                    >
                      <thead>
                        <tr style={{ background: "#f5f5f5" }}>
                          <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ddd" }}>İsim</th>
                          <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ddd" }}>Email</th>
                          <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ddd" }}>Rol</th>
                          <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ddd" }}>Durum</th>
                          <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #ddd" }}>
                            Son Giriş
                          </th>
                          <th style={{ padding: "10px", textAlign: "center", borderBottom: "2px solid #ddd" }}>
                            İzin Ver
                          </th>
                          <th style={{ padding: "10px", textAlign: "center", borderBottom: "2px solid #ddd" }}>
                            Billing
                          </th>
                          <th style={{ padding: "10px", textAlign: "center", borderBottom: "2px solid #ddd" }}>
                            Detay
                          </th>
                          <th style={{ padding: "10px", textAlign: "center", borderBottom: "2px solid #ddd" }}>
                            Giriş Yap
                          </th>
                          <th style={{ padding: "10px", textAlign: "center", borderBottom: "2px solid #ddd" }}>Sil</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients.map((client) => {
                          const isExpanded = selectedClientId === client._id;
                          return (
                            <React.Fragment key={client._id}>
                              <tr
                                style={{
                                  borderBottom: isExpanded ? "none" : "1px solid #eee",
                                  background: isExpanded ? "#e3f2fd" : "transparent",
                                }}
                              >
                                <td style={{ padding: "10px" }}>{client.name || "-"}</td>
                                <td style={{ padding: "10px" }}>{client.email || "-"}</td>
                                <td style={{ padding: "10px" }}>
                                  <span
                                    style={{
                                      padding: "3px 8px",
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      background:
                                        client.role === "owner"
                                          ? "#e3f2fd"
                                          : client.role === "admin"
                                            ? "#fff3e0"
                                            : "#f5f5f5",
                                    }}
                                  >
                                    {client.role || "editor"}
                                  </span>
                                </td>
                                <td style={{ padding: "10px" }}>
                                  <span
                                    style={{
                                      padding: "3px 8px",
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      background: client.status === "active" ? "#e8f5e9" : "#ffebee",
                                    }}
                                  >
                                    {client.status || "pending"}
                                  </span>
                                </td>
                                <td style={{ padding: "10px", fontSize: "12px", color: "#666" }}>
                                  {client.lastLoginAt
                                    ? new Date(client.lastLoginAt).toLocaleString("sv-SE", {
                                        year: "numeric",
                                        month: "2-digit",
                                        day: "2-digit",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "-"}
                                </td>
                                <td style={{ padding: "10px", textAlign: "center" }}>
                                  <input
                                    type="checkbox"
                                    checked={client.permissions?.[PERMISSION_TYPES.ACCESS_TO_GIVE_PERMISSIONS] || false}
                                    onChange={(e) =>
                                      updateClientPermission(
                                        client._id,
                                        PERMISSION_TYPES.ACCESS_TO_GIVE_PERMISSIONS,
                                        e.target.checked,
                                      )
                                    }
                                    disabled={isSavingClient}
                                  />
                                </td>
                                <td style={{ padding: "10px", textAlign: "center" }}>
                                  <input
                                    type="checkbox"
                                    checked={client.permissions?.[PERMISSION_TYPES.ACCESS_TO_BILLING] || false}
                                    onChange={(e) =>
                                      updateClientPermission(
                                        client._id,
                                        PERMISSION_TYPES.ACCESS_TO_BILLING,
                                        e.target.checked,
                                      )
                                    }
                                    disabled={isSavingClient}
                                  />
                                </td>
                                <td style={{ padding: "10px", textAlign: "center" }}>
                                  <button
                                    onClick={() => setSelectedClientId(isExpanded ? null : client._id)}
                                    style={{
                                      background: isExpanded ? "#1976d2" : "none",
                                      color: isExpanded ? "#fff" : "#333",
                                      border: "1px solid #ccc",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {isExpanded ? "Kapat" : "Göster"}
                                  </button>
                                </td>
                                <td style={{ padding: "10px", textAlign: "center" }}>
                                  <button
                                    onClick={() => handleImpersonate(client._id)}
                                    disabled={isImpersonating}
                                    style={{
                                      background: "#1976d2",
                                      color: "#fff",
                                      border: "none",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      cursor: isImpersonating ? "not-allowed" : "pointer",
                                      fontSize: "12px",
                                      opacity: isImpersonating ? 0.6 : 1,
                                    }}
                                  >
                                    {isImpersonating ? "Giriş..." : "Giriş Yap"}
                                  </button>
                                </td>
                                <td style={{ padding: "10px", textAlign: "center" }}>
                                  {client.status === "REMOVED" && (
                                    <button
                                      onClick={() => setDeleteTargetClientId(client._id)}
                                      style={{
                                        background: "#d32f2f",
                                        color: "#fff",
                                        border: "none",
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      DELETE
                                    </button>
                                  )}
                                </td>
                              </tr>

                              {/* Expanded Detail Row */}
                              {isExpanded && (
                                <tr style={{ background: "#e3f2fd" }}>
                                  <td colSpan="10" style={{ padding: "15px", borderBottom: "1px solid #eee" }}>
                                    <div
                                      style={{
                                        background: "#fff",
                                        padding: "15px",
                                        borderRadius: "6px",
                                      }}
                                    >
                                      <Grid container spacing={2}>
                                        {/* Permissions Section */}
                                        <Grid item xs={12} md={6}>
                                          <h4 style={{ marginTop: 0, marginBottom: "12px", fontSize: "14px" }}>
                                            Tüm İzinler
                                          </h4>
                                          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                            {Object.values(PERMISSION_TYPES).map((permissionKey) => {
                                              const hasPermission = client.permissions?.[permissionKey] ?? false;
                                              return (
                                                <div
                                                  key={permissionKey}
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "5px",
                                                    minWidth: "180px",
                                                  }}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={hasPermission}
                                                    onChange={(e) =>
                                                      updateClientPermission(
                                                        client._id,
                                                        permissionKey,
                                                        e.target.checked,
                                                      )
                                                    }
                                                    disabled={isSavingClient}
                                                  />
                                                  <label style={{ fontSize: "12px" }}>{permissionKey}</label>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </Grid>

                                        {/* Schools Section */}
                                        <Grid item xs={12} md={6}>
                                          <h4 style={{ marginTop: 0, marginBottom: "12px", fontSize: "14px" }}>
                                            Okullar ({schools.length})
                                          </h4>
                                          {isSchoolsLoading ? (
                                            <MySkeleton lineCount={3} />
                                          ) : schools.length === 0 ? (
                                            <p style={{ fontSize: "12px", color: "#999", margin: 0 }}>
                                              Bu şirkette okul yok.
                                            </p>
                                          ) : (
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                              {schools.map((school) => (
                                                <div
                                                  key={school._id}
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "5px",
                                                    minWidth: "180px",
                                                  }}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={client.schools?.includes(school._id) || false}
                                                    onChange={() => toggleClientSchool(client._id, school._id)}
                                                    disabled={isSavingClient}
                                                  />
                                                  <label style={{ fontSize: "12px" }}>
                                                    {school.name} ({school.type})
                                                  </label>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </Grid>
                                      </Grid>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <AreYouSureModal
                  showModal={!!deleteTargetClientId}
                  title="Kalıcı Silme"
                  text="Bu kullanıcıyı kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
                  onConfirmed={handleHardDeleteClient}
                  onClose={() => setDeleteTargetClientId(null)}
                />
              </div>
            </Grid>
          </>
        )}
      </Grid>
    </div>
  );
};

export default CompanyManagement;
